const NAV_SELECTOR = '[data-wla-nav]';
const NAV_ITEM_SELECTOR = ':scope > [data-nav-item]';
const DELIMITER_PATTERN = /^(.+?)\s*>\s*(.+)$/;

const normalizeKey = (value) => value.trim().toLowerCase();

const slugify = (value) =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'menu';

const isPlaceholderUrl = (url) => !url || url === '#';

const createChevron = () => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '8');
  svg.setAttribute('viewBox', '0 0 14 8');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('aria-hidden', 'true');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    'M6.9999 5.1714L11.9497 0.22168L13.3639 1.63589L6.9999 7.9999L0.635986 1.63589L2.0502 0.22168L6.9999 5.1714Z'
  );
  path.setAttribute('fill', '#F20000');
  svg.append(path);

  return svg;
};

const getNavItem = (item) => {
  const link = item.querySelector('a');
  const rawLabel = (item.dataset.navLabel || link?.textContent || '').trim();
  const match = rawLabel.match(DELIMITER_PATTERN);

  return {
    item,
    rawLabel,
    label: match ? match[2].trim() : rawLabel,
    parentLabel: match ? match[1].trim() : null,
    url: item.dataset.navUrl || link?.getAttribute('href') || '#',
    slug: item.dataset.navSlug || slugify(rawLabel)
  };
};

const createLink = (href, className, label, ariaLabel) => {
  const link = document.createElement('a');
  link.href = href;
  link.className = className;
  link.textContent = label;
  link.setAttribute('aria-label', ariaLabel || label);
  return link;
};

const createDesktopDropdown = (parent, children) => {
  const slug = slugify(parent.slug || parent.label);
  const trigger = document.createElement(isPlaceholderUrl(parent.url) ? 'button' : 'a');
  const submenu = document.createElement('ul');

  parent.item.className = `nav-submenu-item nav-submenu-item--${slug}`;
  parent.item.removeAttribute('data-nav-item');
  parent.item.innerHTML = '';

  if (trigger.tagName === 'BUTTON') {
    trigger.type = 'button';
  } else {
    trigger.href = parent.url;
  }

  trigger.className = `nav-item__link nav-submenu-item__link nav-submenu-item__link--${slug}`;
  trigger.setAttribute('aria-haspopup', 'true');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', `${parent.label} menu`);
  trigger.append(document.createTextNode(parent.label), createChevron());

  submenu.className = 'submenu';

  children.forEach((child) => {
    const childSlug = slugify(child.slug || child.label);
    const childItem = document.createElement('li');
    childItem.className = 'submenu__item';
    childItem.append(
      createLink(
        child.url,
        `submenu__item-link submenu__item-link--${childSlug}`,
        child.label,
        `${child.label} Navigation link`
      )
    );
    submenu.append(childItem);
  });

  parent.item.append(trigger, submenu);
};

const createMobileDropdown = (parent, children) => {
  const button = document.createElement('button');
  const labelElement = document.createElement(isPlaceholderUrl(parent.url) ? 'span' : 'a');
  const submenu = document.createElement('ul');

  parent.item.className = 'mobile-nav-submenu-item';
  parent.item.removeAttribute('data-nav-item');
  parent.item.innerHTML = '';

  button.type = 'button';
  button.className = 'mobile-nav-submenu-item__button';
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-label', `Open submenu ${parent.label}`);

  if (labelElement.tagName === 'A') {
    labelElement.href = parent.url;
  }
  labelElement.className = 'mobile-nav-submenu-item__link';
  labelElement.textContent = parent.label;
  labelElement.setAttribute('aria-label', `${parent.label} Navigation link`);

  button.append(labelElement, createChevron());

  submenu.className = 'mobile-nav-submenu';

  children.forEach((child) => {
    const childItem = document.createElement('li');
    childItem.className = 'mobile-nav-submenu__item';
    childItem.append(
      createLink(
        child.url,
        'mobile-nav-submenu__item-link',
        child.label,
        `${child.label} Navigation link`
      )
    );
    submenu.append(childItem);
  });

  parent.item.append(button, submenu);
};

const addFooterChildren = (parent, children) => {
  const childItems = children.map((child) => {
    const childItem = document.createElement('li');
    childItem.className = 'footer-subnav-item';
    childItem.append(
      createLink(
        child.url,
        'footer-subnav-item__link',
        child.label,
        `${child.label} Navigation link`
      )
    );
    return childItem;
  });

  parent.item.removeAttribute('data-nav-item');
  parent.item.after(...childItems);
};

const enhanceNavigationList = (list) => {
  if (list.dataset.navDropdownsReady === 'true') {
    return;
  }

  const items = Array.from(list.querySelectorAll(NAV_ITEM_SELECTOR)).map(getNavItem);
  const topLevelItems = new Map();
  const childrenByParent = new Map();

  items.forEach((navItem) => {
    if (navItem.parentLabel) {
      const key = normalizeKey(navItem.parentLabel);
      if (!childrenByParent.has(key)) {
        childrenByParent.set(key, []);
      }
      childrenByParent.get(key).push(navItem);
      return;
    }

    topLevelItems.set(normalizeKey(navItem.label), navItem);
  });

  childrenByParent.forEach((children, parentKey) => {
    const parent = topLevelItems.get(parentKey);

    if (!parent) {
      return;
    }

    children.forEach((child) => child.item.remove());

    if (list.dataset.wlaNav === 'desktop') {
      createDesktopDropdown(parent, children);
    } else if (list.dataset.wlaNav === 'mobile') {
      createMobileDropdown(parent, children);
    } else if (list.dataset.wlaNav === 'footer') {
      addFooterChildren(parent, children);
    }
  });

  list.dataset.navDropdownsReady = 'true';
};

export const initNavigationDropdowns = () => {
  document.querySelectorAll(NAV_SELECTOR).forEach(enhanceNavigationList);
};
